import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';

function fmtDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

function timeRemaining(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m left`;
  return `${hours}h ${minutes}m left`;
}

const MODE_META = {
  full: {
    label: 'AI Integration Mode',
    subtitle: 'Super admin only',
    intro: 'Turn on a temporary key an external AI tool (e.g. Claude) can use to query the database for analysis and propose changes.',
    scopeNote: null,
    rules: [
      'Reads (SELECT/WITH) run immediately — writes never do',
      'Every INSERT/UPDATE/DELETE must be proposed, previewed, and approved below before it touches the database',
      'UPDATE/DELETE must include a WHERE clause and affect no more than 200 rows',
      'One statement per request — no chaining, no stacked queries',
      'Read results are capped at 1,000 rows per query',
      'Credential-like columns (passwords, API keys, tokens) are always hidden',
      'Disabling the mode revokes the key immediately',
    ],
  },
  study_materials: {
    label: 'AI Integration — Study Materials',
    subtitle: 'Super admin only · scoped access',
    intro: 'Turn on a temporary key an external AI tool can use to create and edit Study Materials course content only.',
    scopeNote: 'This key can reach ONLY sm_courses, sm_categories, sm_main_titles, sm_sub_titles and sm_sub_title_content — nothing else in the database is visible or writable through it, no matter what the AI is asked to do.',
    rules: [
      'Scoped to Study Materials only — no other table is reachable, for reads or writes',
      'Reads (SELECT/WITH) run immediately — writes never do',
      'Every INSERT/UPDATE must be proposed, previewed, and approved below before it touches the database',
      'One statement per request — no chaining, no stacked queries',
      'Read results are capped at 1,000 rows per query',
      'Disabling the mode revokes the key immediately',
    ],
  },
};

const OPERATION_META = {
  insert: { label: 'Create', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  update: { label: 'Update', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  delete: { label: 'Delete', badge: 'bg-red-50 text-red-700 border-red-200' },
};

const STATUS_META = {
  pending: { label: 'Pending', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  applied: { label: 'Applied', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', badge: 'bg-gray-100 text-gray-600 border-gray-200' },
  undone: { label: 'Undone', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
};

function RowPreview({ row }) {
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-lg border border-gray-100 bg-gray-50 p-3 sm:grid-cols-3">
      {Object.entries(row).map(([key, value]) => (
        <div key={key} className="min-w-0">
          <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-gray-400">{key}</p>
          <p className="truncate text-xs text-gray-700" title={String(value ?? '')}>{value === null || value === undefined || value === '' ? '—' : String(value)}</p>
        </div>
      ))}
    </div>
  );
}

function ChangeCard({ change, onApprove, onReject, onUndo, actingId, selectable, selected, onToggleSelect }) {
  const opMeta = OPERATION_META[change.operation] || { label: change.operation, badge: 'bg-gray-100 text-gray-600 border-gray-200' };
  const statusMeta = STATUS_META[change.status] || { label: change.status, badge: 'bg-gray-100 text-gray-600 border-gray-200' };
  const isActing = actingId === change.id;
  const rows = change.preview?.affectedRows || [];

  return (
    <div className={`rounded-xl border bg-white p-5 shadow-sm ${selectable && selected ? 'border-indigo-300 ring-1 ring-indigo-200' : 'border-gray-200'}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {selectable && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(change.id)}
              className="h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
          )}
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${opMeta.badge}`}>{opMeta.label}</span>
          <span className="text-sm font-semibold text-gray-900">{change.tableName}</span>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusMeta.badge}`}>{statusMeta.label}</span>
        </div>
        <span className="text-xs text-gray-400">{fmtDateTime(change.proposedAt)}</span>
      </div>

      <pre className="mt-3 whitespace-pre-wrap rounded-lg bg-gray-900 p-3 font-mono text-xs text-gray-100">{change.sqlText}</pre>

      {change.operation === 'insert' ? (
        <p className="mt-3 text-xs text-gray-500">This will insert 1 new row with the values above — no existing rows are affected.</p>
      ) : (
        <div className="mt-3">
          <p className="mb-2 text-xs font-semibold text-gray-600">
            {change.affectedRowCount ?? rows.length} row{(change.affectedRowCount ?? rows.length) === 1 ? '' : 's'} affected:
          </p>
          <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
            {rows.map((row, idx) => <RowPreview key={idx} row={row} />)}
          </div>
        </div>
      )}

      {change.status === 'pending' && (
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onReject(change.id)}
            disabled={isActing}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reject
          </button>
          <button
            type="button"
            onClick={() => onApprove(change.id)}
            disabled={isActing}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isActing ? 'Applying…' : 'Approve'}
          </button>
        </div>
      )}

      {change.status === 'applied' && (
        <div className="mt-4 flex items-center justify-between gap-2">
          <p className="text-xs text-gray-400">
            Applied {fmtDateTime(change.appliedAt)}
            {change.preview?.undoAvailable === false ? ' · undo not available for this table' : ''}
          </p>
          {change.preview?.undoAvailable !== false && (
            <button
              type="button"
              onClick={() => onUndo(change.id)}
              disabled={isActing}
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isActing ? 'Undoing…' : 'Undo'}
            </button>
          )}
        </div>
      )}

      {change.status === 'rejected' && change.errorMessage && (
        <p className="mt-3 text-xs text-red-600">Failed: {change.errorMessage}</p>
      )}
      {change.status === 'undone' && (
        <p className="mt-3 text-xs text-purple-600">Undone {fmtDateTime(change.undoneAt)}</p>
      )}
    </div>
  );
}

export default function AiIntegrationMode({ mode = 'full' }) {
  const meta = MODE_META[mode] || MODE_META.full;
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [busy, setBusy] = useState(false);
  const [hours, setHours] = useState(4);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedInstructions, setCopiedInstructions] = useState(false);
  const [changes, setChanges] = useState([]);
  const [loadingChanges, setLoadingChanges] = useState(true);
  const [hasLoadedChanges, setHasLoadedChanges] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadStatus = () => {
    setLoadingStatus(true);
    axiosInstance.get('/admin/data-tools/ai-integration/status', { params: { mode } })
      .then((resp) => setStatus(resp.data))
      .catch(() => setStatus({ enabled: false }))
      .finally(() => setLoadingStatus(false));
  };

  // Proposals persist and stay reviewable even if the mode is later disabled
  // -- disabling only stops the AI from making NEW calls, it doesn't discard
  // whatever it already proposed while it was on.
  const loadChanges = () => {
    setLoadingChanges(true);
    axiosInstance.get('/admin/data-tools/ai-integration/changes', { params: { mode } })
      .then((resp) => {
        const rows = resp.data?.changes || [];
        setChanges(rows);
        // Drop any selection that's no longer pending (approved/rejected by
        // this action or by someone else since the last load).
        const stillPending = new Set(rows.filter((c) => c.status === 'pending').map((c) => c.id));
        setSelectedIds((prev) => prev.filter((id) => stillPending.has(id)));
      })
      .catch(() => setChanges([]))
      .finally(() => { setLoadingChanges(false); setHasLoadedChanges(true); });
  };

  useEffect(() => { loadStatus(); loadChanges(); }, [mode]);

  const handleApprove = async (id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Apply this change?',
      text: 'This will run the exact statement shown against the live database. This cannot be undone automatically for every table.',
      showCancelButton: true,
      confirmButtonText: 'Approve & Apply',
      confirmButtonColor: '#4f46e5',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setActingId(id);
    try {
      await axiosInstance.post(`/admin/data-tools/ai-integration/changes/${id}/approve`);
      loadChanges();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to apply this change.', 'error');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (id) => {
    setActingId(id);
    try {
      await axiosInstance.post(`/admin/data-tools/ai-integration/changes/${id}/reject`);
      loadChanges();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to reject this proposal.', 'error');
    } finally {
      setActingId(null);
    }
  };

  const handleUndo = async (id) => {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Undo this change?',
      text: 'This restores the previous data for the rows this change affected.',
      showCancelButton: true,
      confirmButtonText: 'Undo',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setActingId(id);
    try {
      await axiosInstance.post(`/admin/data-tools/ai-integration/changes/${id}/undo`);
      loadChanges();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to undo this change.', 'error');
    } finally {
      setActingId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    const pendingIds = pendingChanges.map((c) => c.id);
    const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : pendingIds);
  };

  const summarizeBulkResult = (resp) => {
    const approved = resp?.approved?.length || resp?.rejected?.length || 0;
    const failed = resp?.failed?.length || 0;
    const skipped = resp?.skipped?.length || 0;
    const parts = [`${approved} succeeded`];
    if (failed) parts.push(`${failed} failed`);
    if (skipped) parts.push(`${skipped} skipped (already reviewed)`);
    return parts.join(', ');
  };

  const runBulk = async (endpoint, body, verb) => {
    setBulkBusy(true);
    try {
      const resp = await axiosInstance.post(`/admin/data-tools/ai-integration/changes/${endpoint}`, body);
      setSelectedIds([]);
      loadChanges();
      Swal.fire(verb, summarizeBulkResult(resp.data), (resp.data?.failed?.length ? 'warning' : 'success'));
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || `Failed to ${verb.toLowerCase()}.`, 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    const totalRows = pendingChanges
      .filter((c) => selectedIds.includes(c.id))
      .reduce((sum, c) => sum + (c.affectedRowCount ?? c.preview?.affectedRows?.length ?? 0), 0);
    const result = await Swal.fire({
      icon: 'warning',
      title: `Approve ${selectedIds.length} proposal${selectedIds.length === 1 ? '' : 's'}?`,
      text: `This runs each statement live against the database (~${totalRows} row${totalRows === 1 ? '' : 's'} total). Each is applied independently — one failing will not block the others.`,
      showCancelButton: true,
      confirmButtonText: 'Approve Selected',
      confirmButtonColor: '#4f46e5',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    runBulk('bulk-approve', { ids: selectedIds }, 'Approved');
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;
    const result = await Swal.fire({
      icon: 'warning',
      title: `Reject ${selectedIds.length} proposal${selectedIds.length === 1 ? '' : 's'}?`,
      showCancelButton: true,
      confirmButtonText: 'Reject Selected',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    runBulk('bulk-reject', { ids: selectedIds }, 'Rejected');
  };

  const handleApproveAllPending = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: `Approve all ${pendingChanges.length} pending proposal${pendingChanges.length === 1 ? '' : 's'}?`,
      text: 'This runs every currently pending statement live against the database. Each is applied independently — one failing will not block the others.',
      showCancelButton: true,
      confirmButtonText: 'Approve All Pending',
      confirmButtonColor: '#4f46e5',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    runBulk('bulk-approve', { all: true }, 'Approved');
  };

  const handleEnable = async () => {
    setBusy(true);
    try {
      const resp = await axiosInstance.post('/admin/data-tools/ai-integration/enable', { hours, mode });
      setStatus(resp.data);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to enable AI integration mode.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    const result = await Swal.fire({
      icon: 'warning',
      title: `Disable ${meta.label}?`,
      text: 'The current key will stop working immediately.',
      showCancelButton: true,
      confirmButtonText: 'Disable',
      confirmButtonColor: '#dc2626',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    setBusy(true);
    try {
      const resp = await axiosInstance.post('/admin/data-tools/ai-integration/disable', { mode });
      setStatus(resp.data);
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to disable AI integration mode.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text, setFlag) => {
    await navigator.clipboard.writeText(text);
    setFlag(true);
    setTimeout(() => setFlag(false), 2000);
  };

  const remaining = useMemo(() => timeRemaining(status?.expiresAt), [status?.expiresAt]);
  const pendingChanges = useMemo(() => changes.filter((c) => c.status === 'pending'), [changes]);
  const recentChanges = useMemo(() => changes.filter((c) => c.status !== 'pending'), [changes]);

  return (
    <DashboardLayout title={meta.label}>
      <a href="/admin/data-tools" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-700">
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
        Database Activity
      </a>

      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-50 text-purple-600">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" /></svg>
        </span>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{meta.label}</h1>
          <p className="mt-0.5 text-sm text-gray-500">{meta.subtitle}</p>
        </div>
      </div>

      {meta.scopeNote && (
        <div className="mb-6 rounded-xl border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-800">
          <span className="font-semibold">Scope: </span>{meta.scopeNote}
        </div>
      )}

      {/* Status hero */}
      <div className={`mb-6 rounded-xl border p-5 ${status?.enabled ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-white'}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className={`flex h-3 w-3 shrink-0 rounded-full ${status?.enabled ? 'animate-pulse bg-emerald-500' : 'bg-gray-300'}`} />
            <div>
              <p className={`text-sm font-bold ${status?.enabled ? 'text-emerald-700' : 'text-gray-700'}`}>
                {loadingStatus ? 'Checking status…' : status?.enabled ? 'Active' : 'Disabled'}
              </p>
              <p className="text-xs text-gray-500">
                {status?.enabled ? `Expires ${fmtDateTime(status.expiresAt)} (${remaining})` : 'No external tool currently has access.'}
              </p>
            </div>
          </div>

          {!loadingStatus && !status?.enabled && (
            <div className="flex items-center gap-2">
              <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value={1}>Auto-expire in 1 hour</option>
                <option value={4}>Auto-expire in 4 hours</option>
                <option value={8}>Auto-expire in 8 hours</option>
                <option value={24}>Auto-expire in 24 hours</option>
              </select>
              <button
                type="button"
                onClick={handleEnable}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Enabling…' : 'Enable'}
              </button>
            </div>
          )}
          {!loadingStatus && status?.enabled && (
            <button
              type="button"
              onClick={handleDisable}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-700 shadow-sm hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Disabling…' : 'Disable Now'}
            </button>
          )}
        </div>

        {!loadingStatus && status?.enabled && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-white/70 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Queries run</p>
              <p className="mt-0.5 text-lg font-bold text-gray-900">{status.queryCount ?? 0}</p>
            </div>
            <div className="rounded-lg bg-white/70 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Last used</p>
              <p className="mt-0.5 text-sm font-semibold text-gray-900">{status.lastUsedAt ? fmtDateTime(status.lastUsedAt) : 'Never yet'}</p>
            </div>
            <div className="rounded-lg bg-white/70 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Enabled at</p>
              <p className="mt-0.5 text-sm font-semibold text-gray-900">{fmtDateTime(status.enabledAt)}</p>
            </div>
          </div>
        )}
      </div>

      {!loadingStatus && status?.enabled && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-base font-bold text-gray-900">API Key</h2>
            <p className="mt-1 text-sm text-gray-500">Give this to Claude (or any tool) along with the instructions on the right.</p>
            <div className="mt-4 flex gap-2">
              <input readOnly value={status.apiKey} className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700" />
              <button type="button" onClick={() => copyText(status.apiKey, setCopiedKey)} className="shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50">
                {copiedKey ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-700">
              Treat this key like a password — anyone who has it can read your database until it expires or you disable this mode.
            </div>

            <h3 className="mt-6 text-xs font-bold uppercase tracking-wide text-gray-500">Rules enforced by the server</h3>
            <ul className="mt-3 space-y-2">
              {meta.rules.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600">
                  <svg className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900">Instructions for Claude</h2>
              <button type="button" onClick={() => copyText(status.instructions, setCopiedInstructions)} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                {copiedInstructions ? 'Copied!' : 'Copy instructions'}
              </button>
            </div>
            <p className="mt-1 text-sm text-gray-500">Paste this whole block into your conversation with the AI.</p>
            <textarea readOnly value={status.instructions} rows={18} className="mt-4 w-full resize-y rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-relaxed text-gray-700" />
          </div>
        </div>
      )}

      {!loadingStatus && !status?.enabled && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-10 text-center">
          <svg className="mx-auto h-10 w-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
          <p className="mt-3 text-sm text-gray-500">No AI tool currently has access. Choose an auto-expire window above and enable to generate a key.</p>
        </div>
      )}

      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-bold text-gray-900">Pending Proposals</h2>
            <p className="mt-1 text-sm text-gray-500">Nothing here has touched the database yet — review the exact rows affected before approving.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {pendingChanges.length > 0 && (
              <button
                type="button"
                onClick={handleApproveAllPending}
                disabled={bulkBusy || Boolean(actingId)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Approve All Pending ({pendingChanges.length})
              </button>
            )}
            <button
              type="button"
              onClick={loadChanges}
              disabled={loadingChanges}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className={`h-3.5 w-3.5 ${loadingChanges ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" /></svg>
              {loadingChanges ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        {pendingChanges.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
              <input
                type="checkbox"
                checked={pendingChanges.length > 0 && pendingChanges.every((c) => selectedIds.includes(c.id))}
                onChange={toggleSelectAll}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              Select all
            </label>
            {selectedIds.length > 0 && (
              <>
                <span className="text-xs text-gray-400">{selectedIds.length} selected</span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleBulkReject}
                    disabled={bulkBusy || Boolean(actingId)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Reject Selected
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkApprove}
                    disabled={bulkBusy || Boolean(actingId)}
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {bulkBusy ? 'Working…' : `Approve Selected (${selectedIds.length})`}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-4 space-y-4">
          {!hasLoadedChanges && loadingChanges ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : pendingChanges.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500">
              No pending proposals right now.
            </div>
          ) : (
            pendingChanges.map((change) => (
              <ChangeCard
                key={change.id}
                change={change}
                onApprove={handleApprove}
                onReject={handleReject}
                onUndo={handleUndo}
                actingId={actingId}
                selectable
                selected={selectedIds.includes(change.id)}
                onToggleSelect={toggleSelect}
              />
            ))
          )}
        </div>
      </div>

      {recentChanges.length > 0 && (
        <div className="mt-8">
          <h2 className="text-base font-bold text-gray-900">Recent Activity</h2>
          <p className="mt-1 text-sm text-gray-500">Applied, rejected, and undone proposals — most recent first.</p>
          <div className="mt-4 space-y-4">
            {recentChanges.map((change) => (
              <ChangeCard
                key={change.id}
                change={change}
                onApprove={handleApprove}
                onReject={handleReject}
                onUndo={handleUndo}
                actingId={actingId}
              />
            ))}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
